import { User as OIDCUser, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import create from 'zustand';
import { getRuntimeBackstageSettings, getVariable } from '../backstage/BackstageProvider';
import { RoleType } from './roles';

let host = window.location.protocol.concat('//');
host = host.concat(window.location.hostname);
if (window.location.port) {
  host = host.concat(':');
  host = host.concat(window.location.port);
}

const getConfig = async () => {
  const IDENTITY_CLIENT_ID = await getVariable('authIdentityClientId');
  const AUTH_SERVER_URL = await getVariable('authUrl');
  const REDIRECT_URL = await getVariable('authRedirectUrl');
  const LOGOFF_REDIRECT_URL = await getVariable('authLogoffRedirectUrl');

  const IDENTITY_CONFIG = {
    authority: AUTH_SERVER_URL,
    client_id: IDENTITY_CLIENT_ID,
    redirect_uri: host.concat(REDIRECT_URL),
    post_logout_redirect_uri: host.concat(LOGOFF_REDIRECT_URL),
    response_type: 'code',
    scope: 'openid profile email api.read offline_access IdentityServerApi',
    filterProtocolClaims: true,
    loadUserInfo: true,
    extraQueryParams: {
      setPasswordRedirect: host
    }
  };

  const METADATA_OIDC = {
    issuer: AUTH_SERVER_URL,
    jwks_uri: AUTH_SERVER_URL.concat('/.well-known/openid-configuration/jwks'),
    authorization_endpoint: AUTH_SERVER_URL.concat('/connect/authorize'),
    token_endpoint: AUTH_SERVER_URL.concat('/connect/token'),
    userinfo_endpoint: AUTH_SERVER_URL.concat('/connect/userinfo'),
    end_session_endpoint: AUTH_SERVER_URL.concat('/connect/endsession'),

    /* TODO There is a huge reload loop issue with using this functionality on cookie
         protected browsers (safari, firefox) and modes (chrome incognito)
         check_session_iframe: AUTH_SERVER_URL.concat('/connect/checksession'),
         */
    revocation_endpoint: AUTH_SERVER_URL.concat('/connect/revocation'),
    introspection_endpoint: AUTH_SERVER_URL.concat('/connect/introspect')
  };

  return { IDENTITY_CONFIG, METADATA_OIDC, AUTH_SERVER_URL, IDENTITY_CLIENT_ID };
};

export enum AuthenticationState {
  Authenticated = 'Authenticated',
  Unauthenticated = 'Unauthenticated'
}

export enum Role {
  SuperAdmin = 'SuperAdmin',
  Admin = 'Admin',
  Manager = 'Manager',
  Staff = 'Staff'
}

export const isStaff = (role: Role | undefined) => role && role.includes(Role.Staff);

export const isManagement = (role: Role | undefined) =>
  role && (role.includes(Role.Admin) || role.includes(Role.Manager));

export const isAdmin = (role: Role | undefined) => role === Role.Admin;

export const isSuperAdmin = (role: Role | undefined) => role === Role.SuperAdmin;

export enum Permissions {
  EmployeeCreate = 'Employee.Create',
  EmployeeRead = 'Employee.Read',
  EmployeeReadAll = 'Employee.ReadAll',
  EmployeeUpdate = 'Employee.Update',
  EmployeeDelete = 'Employee.Delete',
  EmployeeDeactivate = 'Employee.Deactivate',
  SettingsCreate = 'Settings.Create',
  TemplateCreate = 'Template.Create',
  TemplateRead = 'Template.Read',
  TemplateUpdate = 'Template.Update',
  TemplateDelete = 'Template.Delete',
  WorkingPatternRead = 'WorkingPattern.Read',
  WorkingPatternUpdate = 'WorkingPattern.Update',
  WorkingPatternCreate = 'WorkingPattern.Create',
  AvailabilityCreate = 'Availability.Create',
  AvailabilityRead = 'Availability.Read',
  AvailabilityUpdate = 'Availability.Update',
  OperatingHoursCreate = 'OperatingHours.Create',
  OperatingHoursRead = 'OperatingHours.Read',
  OperatingHoursUpdate = 'OperatingHours.Update',
  OperatingHoursDelete = 'OperatingHours.Delete',
  OptimiserExecute = 'Optimiser.Execute',
  LocationCreate = 'Location.Create',
  LocationRead = 'Location.Read',
  LocationUpdate = 'Location.Update',
  RosterCreate = 'Roster.Create',
  RosterRead = 'Roster.Read',
  RosterUpdate = 'Roster.Update',
  RosterDelete = 'Roster.Delete',
  SettingsRead = 'Settings.Read',
  SettingsUpdate = 'Settings.Update',
  SettingsDelete = 'Settings.Delete',
  SkillsCreate = 'Skills.Create',
  SkillsRead = 'Skills.Read',
  SkillsUpdate = 'Skills.Update',
  SkillsDelete = 'Skills.Delete',
  StationCreate = 'Station.Create',
  StationRead = 'Station.Read',
  StationUpdate = 'Station.Update',
  StationDelete = 'Station.Delete',
  TargetCreate = 'Target.Create',
  TargetUpdate = 'Target.Update',
  TargetDelete = 'Target.Delete',
  TargetRead = 'Target.Read',
  TaskCreate = 'Task.Create',
  TaskRead = 'Task.Read',
  TaskUpdate = 'Task.Update',
  TaskDelete = 'Task.Delete',
  WorkingPatternDelete = 'WorkingPattern.Delete',
  AvailabilityDelete = 'Availability.Delete',
  LocationDelete = 'Location.Delete',
  WorkingTimeDataCreate = 'WorkingTimeData.Create',
  WorkingTimeDataRead = 'WorkingTimeData.Read',
  WorkingTimeDataReadAll = 'WorkingTimeData.ReadAll',
  WorkingTimeDataUpdate = 'WorkingTimeData.Update',
  WorkingTimeDataCreateSelf = 'WorkingTimeData.CreateSelf',
  WorkingTimeDataReadSelf = 'WorkingTimeData.ReadSelf',
  WorkingTimeDataUpdateSelf = 'WorkingTimeData.UpdateSelf'
}

export const getTenantConfiguration = async (id: string) => {
  try {
    const backstageSettings = getRuntimeBackstageSettings();
    if (!backstageSettings.url || !backstageSettings.environment) {
      return {};
    }

    const url = `${backstageSettings.url}${backstageSettings.environment}/${id}.json`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch tenant configuration from ${url} (HTTP ${response.status})`);
    }

    return await response.json();
  } catch (e) {
    return {};
  }
};

export type User = OIDCUser & {
  profile?: {
    organisation?: string;
    permission: Permissions[];
  };
  delegation?: {
    isDelegating: boolean;
    delegatedTenantName?: string;
    delegatedTenantId?: string;
    delegatedRole?: Role;
  };
};

export interface AuthService {
  state: AuthenticationState;
  user?: User;
  originalRole?: Role; /** The user's original role as provided by OIDC (before delegation) */
  role?: Role; /** The effective role used by the application, taking delegation into account */
  name?: string;
  isDelegatedSuperAdmin: boolean; /** SuperAdmin logged in, currently acting as Admin via delegation */
  userManager: Promise<UserManager>;

  setUser: (user: User) => Promise<void>;
  clearUser: () => Promise<void>;
  restore: () => Promise<void>;

  setDelegation: (delegation: User['delegation']) => void;
  getEffectiveTenantId: () =>
    | string
    | undefined; /** Effective tenant id (delegated tenant if delegating, otherwise own tenant) */
}

const ROLE_KEY = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

export const useAuthenticationService = create<AuthService>((set, get) => ({
  isDelegatedSuperAdmin: false,
  state: AuthenticationState.Unauthenticated,
  restore: async () => {
    const { AUTH_SERVER_URL, IDENTITY_CLIENT_ID } = await getConfig();
    const token = sessionStorage.getItem(`oidc.user:${AUTH_SERVER_URL}:${IDENTITY_CLIENT_ID}`);

    if (!token) {
      set(() => ({ state: AuthenticationState.Unauthenticated }));
      return;
    }

    const user = JSON.parse(token) as User;
    if (user.profile?.organisation) await getTenantConfiguration(user.profile.organisation);

    const delegation: User['delegation'] = { isDelegating: false };
    const oidcRole = user.profile?.[ROLE_KEY] as Role | undefined;

    set(state => {
      const name = (() => {
        const names = user.profile.name as unknown;
        if (typeof names === 'string') return names;
        return (names as string[]).toString().split(',')[1];
      })();
      return {
        ...state,
        user: { ...user, delegation } as User,
        name,
        state: AuthenticationState.Authenticated,
        originalRole: oidcRole,
        isDelegatedSuperAdmin: false,
        role: delegation.isDelegating ? delegation.delegatedRole : oidcRole
      };
    });
  },
  setUser: async (user: User) => {
    if (user.profile.organisation) await getTenantConfiguration(user.profile.organisation);

    const originalRole = user.profile[ROLE_KEY] as Role;

    set(state => {
      const role = user.profile[ROLE_KEY] as Role;
      const name = (() => {
        const names = user.profile.name as unknown;
        if (typeof names === 'string') return names;
        return (names as string[]).toString().split(',')[1];
      })();
      return {
        ...state,
        user,
        state: AuthenticationState.Authenticated,
        originalRole,
        role: originalRole,
        isDelegatedSuperAdmin: false,
        name
      };
    });
  },
  clearUser: async () =>
    await set(state => {
      state.userManager.then(userManager => {
        userManager.clearStaleState();
      });
      return { ...state, user: undefined, state: AuthenticationState.Unauthenticated };
    }),
  userManager: (async () => {
    const { IDENTITY_CONFIG, METADATA_OIDC } = await getConfig();
    return new UserManager({
      ...IDENTITY_CONFIG,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      metadata: { ...METADATA_OIDC }
    });
  })(),
  setDelegation: (delegation: User['delegation']) => {
    set(state => {
      if (!state.user) return state;

      const updatedUser = { ...state.user, delegation } as User;

      const effectiveRole = delegation?.isDelegating ? delegation.delegatedRole : state.originalRole;

      const isDelegatedSuperAdmin =
        delegation?.isDelegating === true &&
        state.originalRole === Role.SuperAdmin &&
        delegation.delegatedRole === Role.Admin;

      return {
        ...state,
        user: updatedUser,
        role: effectiveRole,
        isDelegatedSuperAdmin
      };
    });
  },
  getEffectiveTenantId: () => {
    const { user } = get();
    if (!user) return undefined;

    if (user.delegation?.isDelegating && user.delegation.delegatedTenantId) {
      return user.delegation.delegatedTenantId;
    }

    return user.profile?.organisation;
  }
}));

const authService = useAuthenticationService.getState();
authService.restore();

authService.userManager.then(userManager => {
  const renewAccessToken = async () => {
    try {
      const user = (await userManager.signinSilent()) as User;
      await authService.setUser(user);
    } catch {
      userManager.signinRedirect();
    }
  };

  userManager.events.addAccessTokenExpired(renewAccessToken);

  // In the scenario where the application is in the background (different active tab, device is sleeping, etc) the access token can become expired causing gql errors.
  // Previously we were only relying on "addAccessTokenExpired" to trigger in these scenarios, in order to renew the token, however, unfortunately it doesnt seem to always work.
  // It seems like "addAccessTokenExpiring" does, however, appear to get called in the background.
  // So a workaround to the issue is to renew here. (Note: this should result in the "addAccessTokenExpired" listener never getting fired.)
  userManager.events.addAccessTokenExpiring(renewAccessToken);
});

export const getBaseURL = async () => {
  const url = await getVariable('apiUrl');
  if (!url) throw new Error('GraphQL url is not defined.');
  return url.replace('/graphql.generated', '/api');
};

export const getBaseRESTURL = async () => {
  const url = await getVariable('apiUrl');
  if (!url) {
    throw new Error('url is not defined.');
  }

  return url.replace('/graphql', '/api');
};

export const getDefaultFetchOptions = async () => {
  const { user } = useAuthenticationService.getState();
  const options: { headers: { [key: string]: string } } = {
    headers: { 'content-type': 'application/json', accept: 'application/json' }
  };
  if (user) options.headers['authorization'] = `Bearer ${user.access_token}`;
  const baseUrl = await getBaseURL();
  return { options, baseUrl };
};

export const useIsAdmin = () => {
  const [role] = useAuthenticationService(state => [state.role]);
  return isAdmin(role);
};

export const useIsManagement = () => {
  const [role] = useAuthenticationService(state => [state.role]);
  return isManagement(role);
};
